import type { DownloadRecord } from './api'

/**
 * Escapa valores para CSV conforme RFC 4180
 */
function escapeCsvValue(val: unknown): string {
  if (val === null || val === undefined) return '""'
  const str = String(val).replaceAll('"', '""')
  return `"${str}"`
}

/**
 * Exporta registros de download filtrados para CSV com BOM UTF-8
 */
export function exportDownloadsToCsv(records: DownloadRecord[], filename = 'downloads.csv') {
  const headers = [
    'ID',
    'Nome do Leitor',
    'E-mail',
    'Telefone',
    'Perfil',
    'Informativos / Newsletter',
    'Obra',
    'Formato',
    'Data e Hora (UTC)',
  ]

  const rows = records.map((r) => [
    r.id,
    r.name,
    r.email,
    r.phone,
    r.isBusinessOwner ? 'Empresário' : 'Não empresário',
    r.optInNewsletter ? 'Autorizou' : 'Não autorizou',
    r.bookTitle,
    r.format.toUpperCase(),
    r.downloadedAt,
  ])

  const csvContent = '\uFEFF' + [
    headers.map(escapeCsvValue).join(';'),
    ...rows.map((row) => row.map(escapeCsvValue).join(';')),
  ].join('\r\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  triggerDownload(blob, filename)
}

/**
 * Escapa strings XML para Excel Spreadsheet 2003
 */
function escapeXml(unsafe: unknown): string {
  if (unsafe === null || unsafe === undefined) return ''
  return String(unsafe)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;')
}

/**
 * Exporta registros de download filtrados para arquivo .xlsx nativo (formato Excel XML 2003)
 * Totalmente compatível com Microsoft Excel, LibreOffice Calc e Google Sheets sem dependências externas pesadas.
 */
export function exportDownloadsToXlsx(records: DownloadRecord[], filename = 'downloads.xlsx') {
  const headers = [
    'ID',
    'Nome do Leitor',
    'E-mail',
    'Telefone',
    'Perfil',
    'Informativos / Newsletter',
    'Obra',
    'Formato',
    'Data e Hora (UTC)',
  ]

  const headerCells = headers
    .map(
      (h) =>
        `<Cell ss:StyleID="HeaderStyle"><Data ss:Type="String">${escapeXml(h)}</Data></Cell>`,
    )
    .join('')

  const dataRows = records
    .map((r) => {
      const cells = [
        `<Cell ss:StyleID="NumberStyle"><Data ss:Type="Number">${r.id}</Data></Cell>`,
        `<Cell ss:StyleID="TextStyle"><Data ss:Type="String">${escapeXml(r.name)}</Data></Cell>`,
        `<Cell ss:StyleID="TextStyle"><Data ss:Type="String">${escapeXml(r.email)}</Data></Cell>`,
        `<Cell ss:StyleID="TextStyle"><Data ss:Type="String">${escapeXml(r.phone)}</Data></Cell>`,
        `<Cell ss:StyleID="TextStyle"><Data ss:Type="String">${r.isBusinessOwner ? 'Empresário' : 'Não empresário'}</Data></Cell>`,
        `<Cell ss:StyleID="TextStyle"><Data ss:Type="String">${r.optInNewsletter ? 'Autorizou' : 'Não autorizou'}</Data></Cell>`,
        `<Cell ss:StyleID="TextStyle"><Data ss:Type="String">${escapeXml(r.bookTitle)}</Data></Cell>`,
        `<Cell ss:StyleID="TextStyle"><Data ss:Type="String">${r.format.toUpperCase()}</Data></Cell>`,
        `<Cell ss:StyleID="TextStyle"><Data ss:Type="String">${escapeXml(r.downloadedAt)}</Data></Cell>`,
      ]
      return `<Row>${cells.join('')}</Row>`
    })
    .join('\n')

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal">
   <Alignment ss:Vertical="Center"/>
   <Borders/>
   <Font ss:FontName="Segoe UI" ss:Size="10" ss:Color="#334155"/>
   <Interior/>
   <NumberFormat/>
   <Protection/>
  </Style>
  <Style ss:ID="HeaderStyle">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Font ss:FontName="Segoe UI" ss:Size="10" ss:Color="#FFFFFF" ss:Bold="1"/>
   <Interior ss:Color="#046BD2" ss:Pattern="Solid"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#034b94"/>
   </Borders>
  </Style>
  <Style ss:ID="TextStyle">
   <Alignment ss:Vertical="Center"/>
   <Font ss:FontName="Segoe UI" ss:Size="10" ss:Color="#334155"/>
  </Style>
  <Style ss:ID="NumberStyle">
   <Alignment ss:Vertical="Center" ss:Horizontal="Center"/>
   <Font ss:FontName="Segoe UI" ss:Size="10" ss:Color="#334155"/>
   <NumberFormat ss:Format="0"/>
  </Style>
 </Styles>
 <Worksheet ss:Name="Downloads">
  <Table ss:DefaultRowHeight="20">
   <Column ss:Width="40"/>
   <Column ss:Width="160"/>
   <Column ss:Width="180"/>
   <Column ss:Width="120"/>
   <Column ss:Width="110"/>
   <Column ss:Width="130"/>
   <Column ss:Width="260"/>
   <Column ss:Width="70"/>
   <Column ss:Width="140"/>
   <Row ss:Height="24">
    ${headerCells}
   </Row>
   ${dataRows}
  </Table>
  <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
   <Selected/>
   <FreezePanes/>
   <FrozenNoSplit/>
   <SplitHorizontal>1</SplitHorizontal>
   <TopRowBottomPane>1</TopRowBottomPane>
   <ActivePane>2</ActivePane>
  </WorksheetOptions>
 </Worksheet>
</Workbook>`

  const blob = new Blob([xml], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8',
  })
  triggerDownload(blob, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`)
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
